import React from "react";
import { DISPENSARY_PAGES } from "constants/dispensary";
import { NextPage, NextPageContext } from "next";
import { useSelector } from "react-redux";

import { getDispensary } from "api/requests/getDispensary";
import { Dispensary } from "custom-types/Dispensary";
import { getDomainCountryCode } from "redux/selectors/config";
import { getIsUnclaimed } from "redux/selectors/dispensary";
import { getCoordinates } from "redux/selectors/location";
import { AppStore } from "redux/store";
import logError from "utils/logError";
import { validateDispensary } from "utils/validateDispensary";

import AboutDispensary from "components/Dispensary/AboutDispensary";
import DispensaryHead from "components/Dispensary/DispensaryHead";
import DispensaryHeader from "components/Dispensary/DispensaryHeader";
import DispensaryTabs from "components/Dispensary/DispensaryTabs";
import DoctorClaimPanel from "components/Dispensary/DoctorClaimPanel";

type Props = {
  dispensary: Dispensary;
};

const DoctorPage: NextPage<Props> = ({ dispensary }) => {
  const isUnclaimed = useSelector(getIsUnclaimed);

  return (
    <>
      <DispensaryHead dispensary={dispensary} pageType="doctor" />
      <DispensaryHeader />
      <DispensaryTabs activeTab={DISPENSARY_PAGES.main} />

      {isUnclaimed ? <DoctorClaimPanel /> : <AboutDispensary />}
    </>
  );
};

DoctorPage.getInitialProps = async (context: NextPageContext<AppStore>) => {
  const { query, store, res, req } = context;
  const { slug } = query;

  const countryCode = getDomainCountryCode(store.getState());
  const coordinates = getCoordinates(store.getState());

  try {
    const dispensary = await getDispensary(slug as string, coordinates);

    const { redirect } = await validateDispensary({
      countryCode,
      dispensary,
      query,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- temporarily using non-null assertion before refactoring validateDispensary
      req: req!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- temporarily using non-null assertion before refactoring validateDispensary
      res: res!,
    });

    if (redirect) {
      return;
    }

    store.dispatch({
      dispensary,
      type: "dispensary/load",
    });

    return {
      dispensary,
    };
  } catch (err) {
    logError(err.message || "Unknown error", {
      functionName: "/doctors/[slug] getInitialProps",
    });

    return err;
  }
};

export default DoctorPage;
